import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import ActivityFlowActions from "@/components/ActivityFlowActions";
import { CheckCircle, XCircle, MinusCircle, PaperPlaneTilt, ArrowClockwise } from "@phosphor-icons/react";

const STATUS = {
  delivered: { label: "Email livrat", cls: "text-emerald-400", Icon: CheckCircle },
  failed: { label: "Email eșuat", cls: "text-red-400", Icon: XCircle },
  no_email: { label: "Fără adresă email", cls: "text-muted-foreground", Icon: MinusCircle },
  not_sent: { label: "Fără email", cls: "text-muted-foreground", Icon: MinusCircle },
};

function RecipientBadge({ handoffId, r, onResend, busy }) {
  const s = STATUS[r.email_status] || STATUS.not_sent;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border border-border bg-white/5 px-2 py-0.5 text-[11px] ${s.cls}`} data-testid={`handoff-recipient-${r.id}`} title={s.label}>
      <Icon size={13} weight="fill" /> {r.name} · {s.label}
      {r.email_status === "failed" && (
        <button onClick={() => onResend(handoffId, r.id)} disabled={busy} className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-300 hover:bg-red-500/25 disabled:opacity-50" data-testid={`handoff-resend-${handoffId}-${r.id}`}>
          <ArrowClockwise size={11} /> {busy ? "Se retrimite…" : "Retrimite"}
        </button>
      )}
    </span>
  );
}

export default function ProjectFlowComms({ projectId, projectCode }) {
  const [history, setHistory] = useState([]);
  const [busyKey, setBusyKey] = useState(null);
  const load = () => api.get(`/projects/${projectId}/handoffs`).then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const resend = async (tid, rid) => {
    setBusyKey(`${tid}:${rid}`);
    try {
      const { data } = await api.post(`/projects/${projectId}/handoffs/${tid}/resend`, { recipient_id: rid });
      if (data.ok) toast.success("Email retrimis cu succes");
      else toast.error("Retrimiterea a eșuat din nou");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyKey(null); }
  };

  return (
    <div className="mb-6 rounded-xl border border-border bg-card p-6" data-testid="project-flow-comms">
      <h3 className="mb-1 font-head text-lg font-bold">Comunicare flux de lucru</h3>
      <p className="mb-4 text-sm text-muted-foreground">
        Folosește <b>„Trimite mai departe"</b> ca să transmiți operațiunea către colegii aleși din listă — primesc notificare în aplicație și un email (poți atașa documente din dosar).
        Dacă întâmpini o problemă pe acest proiect, folosește <b>„Escaladează"</b> pentru a anunța conducerea.
      </p>
      <ActivityFlowActions projectId={projectId} projectCode={projectCode} onDone={load} />

      {history.length > 0 && (
        <div className="mt-5 border-t border-border pt-4" data-testid="handoff-history">
          <p className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <PaperPlaneTilt size={14} /> Transmiteri recente
          </p>
          <div className="space-y-3">
            {history.map((h) => (
              <div key={h.id} className="rounded-lg border border-border px-3 py-2.5" data-testid={`handoff-${h.id}`}>
                <div className="mb-1.5 text-[11px] text-muted-foreground">
                  {fmtDate(h.created_at)} · de la <b className="text-foreground/80">{h.from_user_name || "—"}</b>
                  {h.message ? <span> · „{h.message}"</span> : null}
                  {(h.attached_docs || []).length ? <span> · {h.attached_docs.length} doc. atașate</span> : null}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(h.recipients || []).map((r) => (
                    <RecipientBadge key={r.id} handoffId={h.id} r={r} onResend={resend} busy={busyKey === `${h.id}:${r.id}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
